import { useCallback, useEffect, useLayoutEffect, useRef, useState, type RefObject } from 'react';
import { chapterIndexAtPosition } from './conversation-chapters';

/** Native chapter scrolling with position-derived navigation and interruptible automatic advances. */
export function useChapterNavigation(viewportRef: RefObject<HTMLDivElement | null>, activeKey: string | null, visible: boolean, editing: boolean, compact: boolean, followActiveQuestion = false) {
  const previousActive = useRef<string | null>(null);
  const active = useRef(activeKey);
  active.current = activeKey;
  const viewed = useRef<string | null>(activeKey);
  const target = useRef<string | null>(null);
  const layoutDestination = useRef<string | null>(null);
  const focusTarget = useRef<string | null>(null);
  const anchor = useRef<{ key: string; relative: number; width: number; height: number } | null>(null);
  const [viewedKey, setViewedKey] = useState(activeKey);
  const [targetKey, setTargetKey] = useState<string | null>(null);
  const [index, setIndex] = useState(0);
  const [activeAtEnd, setActiveAtEnd] = useState(false);
  const [steps, setSteps] = useState<{ previous: string | null; next: string | null }>({ previous: null, next: null });
  const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
  const nodes = useCallback(() => Array.from(viewportRef.current?.querySelectorAll<HTMLElement>('[data-chapter-key]') ?? []).filter(node => !node.closest('[inert]')), [viewportRef]);
  const offsets = useCallback(() => {
    const viewport = viewportRef.current;
    return viewport ? nodes().map(node => node.getBoundingClientRect().top - viewport.getBoundingClientRect().top + viewport.scrollTop) : [];
  }, [nodes, viewportRef]);
  /** Find distinct reachable boundaries from the intended destination or current native position. */
  const destinations = useCallback(() => {
    const viewport = viewportRef.current;
    const chapters = nodes();
    const positions = offsets();
    const pending = chapters.findIndex(node => node.dataset.chapterKey === target.current);
    const maximum = Math.max(0, (viewport?.scrollHeight ?? 0) - (viewport?.clientHeight ?? 0));
    const origin = Math.max(0, Math.min(pending >= 0 ? positions[pending] ?? 0 : viewport?.scrollTop ?? 0, maximum));
    let previous: string | null = null;
    let next: string | null = null;
    positions.forEach((position, candidate) => {
      const top = Math.max(0, Math.min(position, maximum));
      const key = chapters[candidate]?.dataset.chapterKey ?? null;
      if (top < origin - 2) previous = key;
      if (top > origin + 2 && next === null) next = key;
    });
    return { previous, next };
  }, [nodes, offsets, viewportRef]);
  const refreshSteps = useCallback(() => {
    const next = destinations();
    setSteps(current => current.previous === next.previous && current.next === next.next ? current : next);
  }, [destinations]);
  const update = useCallback(() => {
    const viewport = viewportRef.current;
    if (!viewport) return;
    const positions = offsets();
    const current = chapterIndexAtPosition(positions, viewport.scrollTop, viewport.clientHeight);
    const key = nodes()[current]?.dataset.chapterKey ?? null;
    viewed.current = key;
    setViewedKey(key);
    setIndex(current);
    setActiveAtEnd(nodes().at(-1)?.dataset.chapterKey === active.current && viewport.clientHeight > 0
      && viewport.scrollHeight - viewport.scrollTop - viewport.clientHeight < 2);
    if (key && (!anchor.current || (anchor.current.width === viewport.clientWidth && anchor.current.height === viewport.clientHeight))) {
      anchor.current = { key, relative: viewport.scrollTop - (positions[current] ?? 0), width: viewport.clientWidth, height: viewport.clientHeight };
    }
    const targetIndex = nodes().findIndex(node => node.dataset.chapterKey === target.current);
    const targetTop = Math.max(0, Math.min(positions[targetIndex] ?? 0, viewport.scrollHeight - viewport.clientHeight));
    if (targetIndex >= 0 && Math.abs(targetTop - viewport.scrollTop) < 2) {
      if (focusTarget.current === target.current) nodes()[targetIndex]?.querySelector<HTMLElement>('[data-chapter-heading]')?.focus({ preventScroll: true });
      focusTarget.current = null;
      target.current = null;
      setTargetKey(null);
    }
    refreshSteps();
  }, [nodes, offsets, refreshSteps, viewportRef]);
  const go = useCallback((key: string, behavior: ScrollBehavior = 'smooth', focusHeading = false) => {
    const viewport = viewportRef.current;
    const destination = nodes().findIndex(node => node.dataset.chapterKey === key);
    if (!viewport || destination < 0) return;
    const top = Math.max(0, Math.min(offsets()[destination] ?? 0, viewport.scrollHeight - viewport.clientHeight));
    target.current = key;
    layoutDestination.current = key;
    focusTarget.current = focusHeading ? key : null;
    setTargetKey(key);
    refreshSteps();
    const prefersReducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
    if (typeof viewport.scrollTo === 'function') viewport.scrollTo({ top, behavior: prefersReducedMotion ? 'auto' : behavior });
    else viewport.scrollTop = top;
    if (Math.abs(viewport.scrollTop - top) < 2) update();
  }, [nodes, offsets, refreshSteps, update, viewportRef]);
  const move = useCallback((direction: -1 | 1) => {
    const key = destinations()[direction < 0 ? 'previous' : 'next'];
    if (key) go(key, 'smooth', true);
  }, [destinations, go]);
  /** Finish a path change after retained exit nodes leave the document. */
  const settleLayout = useCallback(() => {
    const destination = layoutDestination.current;
    if (destination) go(destination, 'auto', focusTarget.current === destination);
    layoutDestination.current = null;
  }, [go]);

  useLayoutEffect(() => {
    if (!visible || !activeKey || previousActive.current === activeKey) return;
    const prior = previousActive.current;
    previousActive.current = activeKey;
    const movingAway = target.current !== null && target.current !== prior && target.current !== activeKey;
    if (editing || (!compact && !followActiveQuestion && (movingAway
      || (prior && viewed.current !== prior && target.current !== prior && !activeAtEnd)))) return;
    go(activeKey, prior ? 'smooth' : 'auto');
  }, [activeKey, activeAtEnd, visible, editing, compact, followActiveQuestion, go]);

  useLayoutEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport || compact || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(() => {
      const prior = anchor.current;
      if (!prior || (prior.width === viewport.clientWidth && prior.height === viewport.clientHeight)) return;
      // A returning footer can resize mid-navigation. Finish the chosen destination before restoring a reading anchor.
      if (target.current && nodes().some(node => node.dataset.chapterKey === target.current)) {
        anchor.current = { ...prior, width: viewport.clientWidth, height: viewport.clientHeight };
        go(target.current, 'auto', focusTarget.current === target.current);
        return;
      }
      const destination = nodes().findIndex(node => node.dataset.chapterKey === prior.key);
      if (destination < 0) return;
      const top = Math.max(0, (offsets()[destination] ?? 0) + prior.relative);
      anchor.current = { ...prior, width: viewport.clientWidth, height: viewport.clientHeight };
      if (typeof viewport.scrollTo === 'function') viewport.scrollTo({ top, behavior: 'auto' });
      else viewport.scrollTop = top;
      update();
    });
    observer.observe(viewport);
    return () => observer.disconnect();
  }, [compact, go, nodes, offsets, update, viewportRef]);

  useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport) return undefined;
    const interrupt = () => {
      layoutDestination.current = null;
      if (!target.current) return;
      target.current = null;
      focusTarget.current = null;
      setTargetKey(null);
      viewport.scrollTo?.({ top: viewport.scrollTop, behavior: 'auto' });
      update();
    };
    const interruptKeyboardScroll = (event: KeyboardEvent) => {
      if (event.defaultPrevented || !['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End', ' '].includes(event.key)) return;
      if (event.target instanceof HTMLElement && event.target.closest('textarea, input, button, [contenteditable="true"]')) return;
      interrupt();
    };
    const interruptForFocusedControl = (event: FocusEvent) => {
      if (!focusTarget.current) return;
      const heading = nodes().find(node => node.dataset.chapterKey === focusTarget.current)?.querySelector('[data-chapter-heading]');
      if (event.target !== heading) interrupt();
    };
    const interruptTabNavigation = (event: KeyboardEvent) => {
      if (event.key === 'Tab' && !event.defaultPrevented && focusTarget.current) interrupt();
    };
    // A retiring branch can move a destination after native scrolling has already arrived.
    const settlePath = (event: TransitionEvent) => {
      if (event.propertyName !== 'grid-template-rows' || !(event.target instanceof HTMLElement)
        || !event.target.classList.contains('conversation-chapter-presence')) return;
      const destination = layoutDestination.current;
      if (destination) go(destination, 'auto', focusTarget.current === destination);
    };
    // Input cancels only an explicit advance. The browser owns distance, momentum and nested scrolling.
    viewport.addEventListener('wheel', interrupt, { passive: true });
    viewport.addEventListener('touchstart', interrupt, { passive: true });
    viewport.addEventListener('pointerdown', interrupt, { passive: true });
    viewport.addEventListener('keydown', interruptKeyboardScroll);
    viewport.addEventListener('focusin', interruptForFocusedControl);
    viewport.addEventListener('transitionend', settlePath);
    viewport.ownerDocument.addEventListener('keydown', interruptTabNavigation);
    return () => {
      viewport.removeEventListener('wheel', interrupt);
      viewport.removeEventListener('touchstart', interrupt);
      viewport.removeEventListener('pointerdown', interrupt);
      viewport.removeEventListener('keydown', interruptKeyboardScroll);
      viewport.removeEventListener('focusin', interruptForFocusedControl);
      viewport.removeEventListener('transitionend', settlePath);
      viewport.ownerDocument.removeEventListener('keydown', interruptTabNavigation);
    };
  }, [go, nodes, update, viewportRef]);

  const browsing = !compact && !activeAtEnd && !!viewedKey && viewedKey !== activeKey && targetKey !== activeKey && viewedKey !== previousActive.current;
  return { index, browsing, update, go, move, settleLayout, canPrevious: steps.previous !== null, canNext: steps.next !== null, nodes, reduced };
}
